// =============================================================================
// features/sales/campaigns/wizard/steps/Step1Objective.tsx — Etapa 1:
// nome, descrição, produto, proprietário, prioridade, cidade (F35-S08, doc 26
// §6.6). Cria o rascunho no servidor na 1ª vez que o gestor avança — dali em
// diante toda etapa seguinte só faz PATCH no mesmo id (retomável).
//
// "Proprietário": omitir assume o próprio ator (mesma convenção de
// CampaignCreateBodySchema). O seletor explícito só aparece quando
// `/api/admin/users` responde (degrada graciosamente — mesmo padrão de
// "Produto" em CampaignsFilters.tsx: um gestor comercial comum pode não ter
// `admin_users:read`).
// =============================================================================

import * as React from 'react';

import { Input } from '../../../../../components/ui/Input';
import { Select } from '../../../../../components/ui/Select';
import { useUsersWithoutAgent } from '../../../../../hooks/admin/useAgents';
import { useProducts } from '../../../../../hooks/admin/useProducts';
import { useCitiesList } from '../../../../../hooks/useCitiesList';
import { useAuth } from '../../../../../lib/auth-store';
import { FormTextarea } from '../../../components/FormTextarea';
import { WizardStepShell } from '../components/WizardStepShell';
import type { ObjectiveValues } from '../types';

export interface Step1ObjectiveProps {
  values: ObjectiveValues;
  onChange: (values: ObjectiveValues) => void;
  onContinue: () => void;
  isPending: boolean;
  errorMessage: string | null;
}

export function Step1Objective({
  values,
  onChange,
  onContinue,
  isPending,
  errorMessage,
}: Step1ObjectiveProps): React.JSX.Element {
  const { user } = useAuth();
  const { cities, isLoading: citiesLoading } = useCitiesList();
  const { data: productsData, isError: productsError } = useProducts({ limit: 100 });
  const { users, isLoading: usersLoading } = useUsersWithoutAgent();

  const [touched, setTouched] = React.useState(false);

  const scopedCities = React.useMemo(() => {
    if (user?.cityScopeIds === null || user?.cityScopeIds === undefined) return cities;
    const allowed = new Set(user.cityScopeIds);
    return cities.filter((c) => allowed.has(c.id));
  }, [cities, user?.cityScopeIds]);

  const nameError =
    touched && values.name.trim().length === 0 ? 'Dê um nome para a campanha.' : undefined;
  const cityError =
    touched && values.cityId === '' ? 'Escolha a cidade desta campanha.' : undefined;

  const canContinue = values.name.trim().length > 0 && values.cityId !== '';

  function handleContinue(): void {
    setTouched(true);
    if (!canContinue) return;
    onContinue();
  }

  return (
    <WizardStepShell
      title="Objetivo da campanha"
      description="Nome, público-alvo geral e quem responde por ela — o essencial para identificar a campanha nas listas."
      onContinue={handleContinue}
      continueDisabled={citiesLoading}
      isPending={isPending}
      errorMessage={errorMessage}
    >
      <Input
        id="wizard-obj-name"
        label="Nome da campanha"
        required
        placeholder="Ex.: Renegociação — parcelas em atraso (agosto)"
        value={values.name}
        error={nameError}
        onChange={(e) => onChange({ ...values, name: e.target.value })}
        onBlur={() => setTouched(true)}
      />

      <FormTextarea
        id="wizard-obj-description"
        label="Descrição"
        placeholder="Contexto para quem for trabalhar essa carteira (opcional)"
        rows={3}
        value={values.description}
        onChange={(e) => onChange({ ...values, description: e.target.value })}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          id="wizard-obj-city"
          label="Cidade"
          required
          placeholder={citiesLoading ? 'Carregando cidades…' : 'Selecione a cidade'}
          disabled={citiesLoading}
          options={scopedCities.map((c) => ({ value: c.id, label: `${c.name} — ${c.state_uf}` }))}
          value={values.cityId}
          error={cityError}
          hint="Define o escopo do público — só clientes desta cidade entram na campanha."
          onChange={(e) => onChange({ ...values, cityId: e.target.value })}
          onBlur={() => setTouched(true)}
        />

        {!productsError && (
          <Select
            id="wizard-obj-product"
            label="Produto"
            placeholder="Sem produto associado"
            options={(productsData?.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            value={values.productId}
            onChange={(e) => onChange({ ...values, productId: e.target.value })}
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          id="wizard-obj-priority"
          type="number"
          label="Prioridade"
          min={0}
          max={1000}
          hint="Maior número desempata contra outras campanhas na fila compartilhada."
          value={values.priority}
          onChange={(e) => onChange({ ...values, priority: Number(e.target.value) || 0 })}
        />

        {!usersLoading && users.length > 0 && (
          <Select
            id="wizard-obj-owner"
            label="Responsável"
            placeholder="Eu mesmo"
            hint="Deixe em branco para você assumir a campanha."
            options={users.map((u) => ({ value: u.id, label: u.fullName }))}
            value={values.ownerUserId}
            onChange={(e) => onChange({ ...values, ownerUserId: e.target.value })}
          />
        )}
      </div>
    </WizardStepShell>
  );
}
